import { useEffect, useMemo } from 'react';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';

import {
  DIRECT_LINE_CONNECTION_ERROR_EVENT,
  DIRECT_LINE_CONNECTION_RECOVERED_EVENT,
  DIRECT_LINE_CONNECTION_STATUS,
  DIRECT_LINE_TOKEN_EXPIRED_EVENT,
} from '../shared/directLine';
import {
  getDirectLineDomain,
  isUsingLocalDirectLine,
} from '../transport/directLineDomain';
import {
  createDirectLineOptions,
  getCreateDirectLineFactory,
} from '../transport/directLineHelpers';
import WebChatFrameworkBase from '../transport/webChatFrameworkBase';
import { LOADING as TOKEN_LOADING } from '../utils/loadingStatus';
import { getConversationIdKey } from '../utils/sessionStorage';
import useChatbotToken from './useChatbotToken';
import useWebChatStore from './useWebChatStore';

/**
 * Owns the Direct Line connection: fetches the chatbot token, builds the
 * Direct Line instance once the token arrives (null until then), and
 * creates the Web Chat store. tokenLoadingStatus is returned so the caller
 * can drive loading/error UI.
 */
export default function useDirectLineConnection() {
  const { useToggleValue, TOGGLE_NAMES } = useFeatureToggle();
  const isSessionPersistenceEnabled = Boolean(
    useToggleValue(TOGGLE_NAMES.virtualAgentChatbotSessionPersistenceEnabled),
  );
  const {
    code,
    expired,
    features,
    loadingStatus: tokenLoadingStatus,
    token,
  } = useChatbotToken();

  const directLine = useMemo(() => {
    if (!token) {
      return null;
    }

    const conversationId = getConversationIdKey();
    const options = createDirectLineOptions({
      conversationId,
      domain: getDirectLineDomain(),
      isLocalDirectLine: isUsingLocalDirectLine(),
      token,
    });
    const createDirectLineFactory = getCreateDirectLineFactory({
      createDirectLine: WebChatFrameworkBase.createDirectLine,
    });

    return createDirectLineFactory(options);
  }, [token]);

  // Watch transport status, distinguish token expiry from connection failure,
  // and end this Direct Line instance after either terminal state.
  useEffect(() => {
    const connectionStatus$ = directLine?.connectionStatus$;
    if (
      !connectionStatus$ ||
      typeof connectionStatus$.subscribe !== 'function'
    ) {
      return undefined;
    }

    let recoveryRequested = false;
    const subscription = connectionStatus$.subscribe(status => {
      if (status === DIRECT_LINE_CONNECTION_STATUS.CONNECTED) {
        recoveryRequested = false;
        window.dispatchEvent(new Event(DIRECT_LINE_CONNECTION_RECOVERED_EVENT));
        return;
      }

      if (
        recoveryRequested ||
        ![
          DIRECT_LINE_CONNECTION_STATUS.EXPIRED_TOKEN,
          DIRECT_LINE_CONNECTION_STATUS.FAILED_TO_CONNECT,
        ].includes(status)
      ) {
        return;
      }

      recoveryRequested = true;
      const eventName =
        status === DIRECT_LINE_CONNECTION_STATUS.EXPIRED_TOKEN
          ? DIRECT_LINE_TOKEN_EXPIRED_EVENT
          : DIRECT_LINE_CONNECTION_ERROR_EVENT;
      window.dispatchEvent(new Event(eventName));
      if (typeof directLine?.end === 'function') {
        directLine.end();
      }
    });

    return () => {
      if (typeof subscription?.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [directLine]);

  const webChatStore = useWebChatStore({
    code,
    createStore: WebChatFrameworkBase.createStore,
    features,
    freeze: tokenLoadingStatus === TOKEN_LOADING || expired,
    isMobile: false,
    isSessionPersistenceEnabled,
  });

  return { directLine, expired, tokenLoadingStatus, webChatStore };
}
